// ─────────────────────────────────────────────────────────────
// Centralized routing: SEGMENTS + PATHS + ROUTE_LABELS
//
// SEGMENTS = relative path segments for <Route path="...">
// PATHS    = absolute paths for navigation (navigate, Link, Navigate)
//
// Feature-specific arrays (STEPS_NEGOTIATIONS, ORDER_FORMS, etc.)
// stay in their feature files but import segments from here.
// ─────────────────────────────────────────────────────────────

// ── Actions ──

export const ROUTE_ACTIONS = {
  create: 'create',
  edit: 'edit',
  view: 'view',
  validate: 'validate'
} as const

export type RouteAction = (typeof ROUTE_ACTIONS)[keyof typeof ROUTE_ACTIONS]

// ── Segments (relative paths for <Route path="...">) ──

export const SEGMENTS = {
  auth: {
    root: 'auth',
    login: 'login',
    register: 'register',
    forgotPassword: 'forgot-password',
    forgotPasswordSuccess: 'forgot-password-success',
    resetPassword: 'reset-password/:id/:token',
    resetPasswordV2: 'reset-password/v2',
    twoFactor: 'two-factor',
    logout: 'logout'
  },

  admin: {
    root: 'admin',
    unauthorized: 'unauthorized',
    dashboard: 'dashboard',
    clients: 'clients',
    business: 'business',
    negotiations: 'negotiations',
    creditos: 'creditos',
    inventory: 'inventory',
    deliveries: 'deliveries',
    reports: 'reports',
    config: 'config',
    users: 'user',
    changelog: 'changelog',
    flows: 'flows',
    revalidation: 'revalidation'
  },

  actions: {
    create: 'create',
    edit: 'edit',
    view: 'view',
    new: 'new',
    id: 'id'
  },

  business: {
    users: 'users',
    roles: 'roles',
    companies: 'companies',
    agencies: 'agencies',
    societies: 'societies',
    projects: 'projects',
    properties: 'properties',
    representants: 'representants',
    countries: 'countries',
    states: 'states',
    workflows: 'workflows',
    creditProcess: 'credit-process',
    professions: 'professions',
    typeProperties: 'type-properties',
    clientConfig: 'client-config'
  },

  clientPortal: {
    root: 'c',
    approvals: 'approvals',
    forms: 'forms'
  },

  account: {
    root: 'account',
    general: 'general',
    security: 'security',
    apiTokens: 'api-tokens'
  },

  clients: {
    individual: 'individual',
    juridical: 'juridical'
  },

  negotiations: {
    assignment: 'assignment',
    detailsNegotiation: 'details-negotiation',
    paymentAgreement: 'payment-agreement',
    productionOrder: 'production-order',
    referralProgram: 'referral-program',
    reservationSending: 'reservation-sending',
    extendedDueDiligence: 'extended-due-diligence',
    print: 'print',
    historyStates: 'history-states',
    attachments: 'attachments',
    /**
     * La previsualización de la app del cliente. En español como el resto de los segmentos que ve
     * el usuario, y sin `preview`: lo que se abre es la vista MÓVIL de esta negociación.
     */
    mobilePreview: 'vista-movil'
  },

  deliveries: {
    deliveryForm: 'delivery-form',
    contactUpdate: 'contact-update',
    print: 'print'
  },

  individual: {
    feicDataForm: 'feic-dataform',
    feicCompany: 'feic-company',
    feicDataPep: 'feic-datapep',
    feicApplication: 'feic-application',
    feicInfoEconomic: 'feic-infoeconomic',
    print: 'print',
    attachments: 'attachments',
    feic: 'feic',
    equityStatement: 'equity-statement',
    legalRepresentatives: 'legal-representatives',
    historyNegotiations: 'history-negotiations',
    reviewHistory: 'historial'
  },

  juridical: {
    feicDataForm: 'feic-data-form',
    feicLegalRepresentative: 'feic-legal-representative',
    feicEconomicInfo: 'feic-economic-info',
    equityStatement: 'equity-statement',
    attachments: 'attachments',
    feic: 'feic',
    print: 'print',
    historyNegotiations: 'history-negotiations'
  },

  // Sub-rutas del modo "Validación de expediente" (portal). Namespace PROPIO del review,
  // compartido por individual y jurídica: el shell (client-portal/review) es agnóstico al
  // tipo de persona y ramifica por `clientType`. Los valores coinciden con los del FEIC por
  // continuidad de URLs, pero son un namespace independiente (no acoplar a `individual`).
  review: {
    applicant: 'feic-dataform',
    company: 'feic-company',
    pep: 'feic-datapep',
    economic: 'feic-infoeconomic',
    equity: 'equity-statement',
    legalRep: 'legal-representatives',
    documents: 'attachments',
    history: 'historial'
  },

  // Sub-rutas del hub "Portal del cliente" (montado en la ruta `validate`). Namespace propio,
  // agnóstico al tipo de persona: el índice es el Resumen; `revisar/*` es el drill-in de la
  // revisión por campo (el shell de review vive bajo aquí).
  portal: {
    flujos: 'flujos',
    revisar: 'revisar'
  },

  roles: {
    new: 'new',
    id: ':id'
  },

  projects: {
    new: 'new',
    id: ':id'
  },

  reports: {
    productAccountReport: 'product-account-report'
  }
} as const

// ── Route labels (for breadcrumbs / UI display) ──

export const ROUTE_LABELS: Record<string, string> = {
  login: 'Iniciar sesión',
  'two-factor': 'Verificación en dos pasos',
  'forgot-password': 'Recuperar contraseña',
  'forgot-password-success': 'Correo de recuperación enviado',
  'reset-password': 'Restablecer contraseña',
  authorize: 'Autorizar reserva',
  approvals: 'Aprobación de documento',
  forms: 'Formulario del cliente',
  admin: 'Inicio',
  business: 'Datos de la empresa',
  clients: 'Clientes',
  'app-accounts': 'Cuentas de la app',
  negotiations: 'Negociaciones',
  creditos: 'Créditos',
  deliveries: 'Postventa',
  config: 'Configuración',
  dashboard: 'Dashboard',
  inventory: 'Inventario',
  reports: 'Reportes',
  changelog: 'Changelog',
  flows: 'Flujos',
  revalidation: 'Revalidación',
  account: 'Cuenta',
  individual: 'Persona individual',
  juridical: 'Persona jurídica',
  create: 'Crear',
  edit: 'Editar',
  view: 'Ver',
  new: 'Nuevo',
  users: 'Usuarios',
  roles: 'Roles',
  companies: 'Empresas',
  agencies: 'Agencias',
  societies: 'Sociedades',
  projects: 'Proyectos',
  properties: 'Inmuebles',
  representants: 'Representantes legales',
  countries: 'Países',
  states: 'Estados',
  workflows: 'Flujos de trabajo',
  'credit-process': 'Configuración de Créditos',
  professions: 'Profesiones',
  'type-properties': 'Subtipos',
  'client-config': 'Configuración de clientes',
  'product-account-report': 'Reporte de cuentas',
  general: 'General',
  security: 'Seguridad',
  'api-tokens': 'API tokens',
  assignment: 'Asignación',
  'details-negotiation': 'Detalles',
  'payment-agreement': 'Acuerdo de pago',
  'production-order': 'Orden de producción',
  'referral-program': 'Programa de referidos',
  'reservation-sending': 'Envío de reserva',
  'extended-due-diligence': 'Due diligence',
  print: 'Imprimir',
  'history-states': 'Historial de estados',
  attachments: 'Adjuntos',
  'vista-movil': 'Vista móvil',
  'delivery-form': 'Formulario de entrega',
  'contact-update': 'Actualización de contacto',
  // Review / validation mode
  validate: 'Validación de expediente',
  historial: 'Historial',
  docs: 'Documentos',
  // Portal del cliente (hub)
  flujos: 'Flujos',
  revisar: 'Revisar expediente',
  // Individual sub-routes
  'feic-dataform': 'Datos personales',
  'feic-datapep': 'PEP',
  'feic-application': 'Representantes legales',
  'feic-infoeconomic': 'Información económica',
  'equity-statement': 'Declaración patrimonial',
  'history-negotiations': 'Historial de negociaciones',
  feic: 'FEIC',
  // Juridical sub-routes
  'feic-data-form': 'Datos de la empresa',
  'feic-legal-representative': 'Representante legal',
  'feic-economic-info': 'Información económica',
  // Misc
  history: 'Historial',
  unauthorized: 'Sin autorización',
  user: 'Usuarios'
}

// ── Absolute paths (for navigate, Link, Navigate) ──

export const PATHS = {
  // ── Auth (público) ──
  auth: {
    login: '/auth/login',
    register: '/auth/register',
    forgotPassword: '/auth/forgot-password',
    forgotPasswordSuccess: '/auth/forgot-password-success',
    resetPassword: '/auth/reset-password',
    resetPasswordV2: '/auth/reset-password/v2',
    twoFactor: '/auth/two-factor'
  },

  // ── Admin (privado) ──
  admin: {
    root: '/admin',
    dashboard: '/admin/dashboard',
    unauthorized: '/admin/unauthorized',
    changelog: '/admin/changelog',

    // Flujos (motor de flujos)
    flows: {
      root: '/admin/flows',
      new: '/admin/flows/new',
      detail: (id: string) => `/admin/flows/${id}`
    },

    // Dashboard de revalidación
    revalidation: {
      root: '/admin/revalidation'
    },

    // Clientes
    clients: {
      root: '/admin/clients',
      individual: {
        create: '/admin/clients/individual/create',
        edit: (id: string) => `/admin/clients/individual/edit/${id}`,
        view: (id: string) => `/admin/clients/individual/view/${id}`,
        validate: (id: string) => `/admin/clients/individual/validate/${id}`
      },
      juridical: {
        create: '/admin/clients/juridical/create',
        edit: (id: string) => `/admin/clients/juridical/edit/${id}`,
        view: (id: string) => `/admin/clients/juridical/view/${id}`,
        validate: (id: string) => `/admin/clients/juridical/validate/${id}`
      },
      byType: (type: string, action: RouteAction, id: string) =>
        `/admin/clients/${type}/${action}/${id}`
    },

    // Negociaciones
    negotiations: {
      root: '/admin/negotiations',
      create: '/admin/negotiations/create',
      edit: (id: string) => `/admin/negotiations/edit/${id}`,
      view: (id: string) => `/admin/negotiations/view/${id}`,
      /**
       * El tablero filtrado por un estado. Toma el `sta_`, no el `_id`.
       *
       * ⚠️ **Estuvo roto y en silencio**: el parámetro decía `state` y el tablero
       * lee `estado`, así que el enlace abría el listado SIN filtrar — nadie ve
       * un error, sólo la lista entera. Y el `_id` crudo que le pasaba el
       * dashboard tampoco habría filtrado nada contra v2, que compara `sta_`.
       * Además abre en la pestaña «Todos»: filtrar por un estado finalizado
       * dentro de la pestaña «En curso» —la de por defecto— no devuelve nada.
       */
      withState: (stateId: string) =>
        `/admin/negotiations?tab=all&estado=${encodeURIComponent(stateId)}`,
      step: (action: RouteAction, id: string, step: string) =>
        `/admin/negotiations/${action}/${id}/${step}`
    },

    credits: {
      root: '/admin/creditos',
      detail: (id: string) => `/admin/creditos/${id}`,
      /**
       * Abre en Créditos la fila que representa a la negociación recién cerrada.
       *
       * `codigo` no es decorativo: limita el listado a esa negociación para que `negociacion`
       * pueda resolver su panel aunque la fila no estuviera entre las primeras 25 del tablero.
       */
      fromNegotiation: (id: string, code: number) =>
        `/admin/creditos?negociacion=${encodeURIComponent(id)}&codigo=${encodeURIComponent(String(code))}`
    },

    // Entregas / Postventa
    deliveries: {
      root: '/admin/deliveries',
      fromNegotiation: (id: string, code: number) =>
        `/admin/deliveries?negociacion=${encodeURIComponent(id)}&codigo=${encodeURIComponent(String(code))}`,
      create: (id: string) => `/admin/deliveries/create/${id}`,
      edit: (id: string) => `/admin/deliveries/edit/${id}`,
      view: (id: string) => `/admin/deliveries/view/${id}`,
      step: (action: RouteAction, id: string, step: string) =>
        `/admin/deliveries/${action}/${id}/${step}`
    },

    // Empresa (business config)
    business: {
      root: '/admin/business',
      users: '/admin/business/users',
      roles: '/admin/business/roles',
      companies: '/admin/business/companies',
      agencies: '/admin/business/agencies',
      societies: '/admin/business/societies',
      projects: {
        root: '/admin/business/projects',
        // Alta/edición viven en el side panel; la única página con :id es docs.
        documents: (id: string) => `/admin/business/projects/${id}/docs`
      },
      properties: '/admin/business/properties',
      typeProperties: {
        root: '/admin/business/type-properties',
        // Alta/edición viven en el side panel; la única página con :id es docs.
        documents: (id: string) => `/admin/business/type-properties/${id}/docs`
      },
      representants: '/admin/business/representants',
      states: '/admin/business/states',
      workflows: '/admin/business/workflows',
      creditProcess: '/admin/business/credit-process',
      professions: '/admin/business/professions',
      clientConfig: '/admin/business/client-config',
      byPermission: (route: string) => `/admin/business/${route}`
    },

    // Cuenta
    account: {
      general: '/admin/account/general',
      security: '/admin/account/security',
      apiTokens: '/admin/account/api-tokens'
    },

    // Reportes
    reports: {
      root: '/admin/reports',
      productAccountReport: '/admin/reports/product-account-report'
    }
  }
} as const
